import { useState } from "react";
import type { TFunction } from "i18next";
import { Link } from "@tanstack/react-router";
import { createColumnHelper, type SortingState } from "@tanstack/react-table";
import { CalendarDays, Check, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { exportHref } from "@jbnl/api-client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DataTable,
  DataTableSkeleton,
  dataTableFeatures,
} from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pager } from "@/components/ui/pager";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { FormActions, Toolbar } from "@jbnl/shared-ui";
import { ListDetail } from "@/components/ListDetail";
import { FULL_PAGE_SIZE } from "@/lib/paging";
import {
  kiesSeizoen,
  onthoudGeselecteerdSeizoenId,
  useSeizoenen,
  useSeizoenenPaged,
} from "@/features/session/useSeizoenen";
import {
  useCreateSeizoen,
  useDeleteSeizoen,
  useUpdateSeizoen,
  type Seizoen,
} from "./useWedstrijden";
import { datumKort } from "./datum";

const helper = createColumnHelper<typeof dataTableFeatures, Seizoen>();

/** The `sortBy` value the API understands for each sortable column. */
const SORT_BY: Record<string, string> = { name: "name", periode: "startDate" };

const kolommen = (
  t: TFunction<"wedstrijden">,
  geselecteerdId: string | undefined,
) =>
  helper.columns([
    helper.accessor("name", {
      header: t("seizoenen.seizoen"),
      cell: (info) => <span className="font-medium">{info.getValue()}</span>,
    }),
    helper.accessor(
      (seizoen) =>
        t("seizoenen.periodeWaarde", {
          start: datumKort(seizoen.startDate),
          eind: datumKort(seizoen.endDate),
        }),
      { id: "periode", header: t("seizoenen.periode") },
    ),
    // A mark, never a control: the row is already what you click to pick a seizoen
    // (`components/ui/data-table.tsx` puts the click on the row for exactly this reason - a
    // button in a cell would be a second control competing with it). Clicking a row both opens
    // it here and makes it the seizoen every other screen defaults to; this cell just says which
    // row that was, for whoever did not click the one they are now looking at.
    helper.display({
      id: "geselecteerd",
      header: () => (
        <span className="sr-only">{t("seizoenen.geselecteerdKolom")}</span>
      ),
      cell: ({ row }) =>
        row.original.id === geselecteerdId ? (
          <Badge variant="outline">
            <Check aria-hidden />
            {t("seizoenen.geselecteerdBadge")}
          </Badge>
        ) : null,
    }),
  ]);

/**
 * Creating a seizoen copies nothing: niveaus, klassen and functies are global now (ADR-0029).
 * Clicking a row also makes that seizoen the one every other screen falls back to for the rest
 * of the browser tab (`kiesSeizoen`) - the only place that choice is made (ADR-0028).
 */
export function Seizoenen({
  seizoenId,
  onKies,
  pagina,
  onPaginaChange,
}: {
  seizoenId: string | undefined;
  onKies: (id?: string) => void;
  pagina: number | undefined;
  onPaginaChange: (pagina: number) => void;
}) {
  const { t } = useTranslation("wedstrijden");
  // Finds the selected seizoen for the detail pane - independent of the list's own page, so
  // choosing one on page 1 and then paging the list to page 2 does not lose the detail pane.
  const { data: seizoenen } = useSeizoenen();
  const seizoen = seizoenen?.find((s) => s.id === seizoenId);

  // The seizoen every other screen now defaults to, by the same fallback `kiesSeizoen` gives
  // them: an explicit pick if one was ever made, otherwise the running seizoen - never nothing,
  // since something is always in effect elsewhere the moment this list has loaded. Storage is
  // read fresh on every render rather than held in state, since writing to it does not itself
  // make React notice a change; `herlees` is what forces the re-render a click needs.
  const [, herlees] = useState(0);
  const geselecteerdId = kiesSeizoen(seizoenen)?.id;

  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const sort = sorting[0];

  const { data: page, isPending } = useSeizoenenPaged(
    pagina,
    FULL_PAGE_SIZE,
    search,
    sort && SORT_BY[sort.id],
    sort?.desc ?? true,
  );

  const columns = kolommen(t, geselecteerdId);

  // Opening a row for editing and picking it for every other screen are the same click - not
  // two things a beheerder has to do separately to get here.
  function kies(id?: string) {
    if (id) {
      onthoudGeselecteerdSeizoenId(id);
      herlees((n) => n + 1);
    }
    onKies(id);
  }

  return (
    <>
      <Toolbar>
        <NieuwSeizoen onAangemaakt={onKies} />
      </Toolbar>

      <ListDetail
        selected={Boolean(seizoen)}
        backLabel={t("seizoenen.terug")}
        onBack={() => onKies()}
        list={
          <SeizoenenLijst
            t={t}
            exportLink={exportHref("/api/seizoenen/export", {
              query: {
                search,
                sortBy: sort && SORT_BY[sort.id],
                sortDescending: sort?.desc ?? true,
              },
            })}
            columns={columns}
            page={page}
            isPending={isPending}
            selectedId={seizoen?.id}
            sorting={sorting}
            onSortingChange={setSorting}
            search={search}
            // A search narrows the set the server counts, so page 2 of the old set would
            // otherwise land past the end of the new, shorter one.
            onSearchChange={(value) => {
              setSearch(value);
              onPaginaChange(1);
            }}
            onSelect={kies}
            onPaginaChange={onPaginaChange}
          />
        }
        detail={
          seizoen ? (
            // Keyed on the id: the form is state seeded from the record, so choosing another
            // seizoen resets it by remounting rather than by an effect that would also fire on
            // every background refetch.
            <SeizoenDetail
              key={seizoen.id}
              seizoen={seizoen}
              onVerwijderd={() => onKies()}
            />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  {t("seizoenen.kies")}
                </p>
              </CardContent>
            </Card>
          )
        }
      />
    </>
  );
}

function SeizoenenLijst({
  t,
  columns,
  page,
  isPending,
  selectedId,
  sorting,
  onSortingChange,
  search,
  onSearchChange,
  onSelect,
  onPaginaChange,
  exportLink,
}: {
  t: TFunction<"wedstrijden">;
  columns: ReturnType<typeof kolommen>;
  page: ReturnType<typeof useSeizoenenPaged>["data"];
  isPending: boolean;
  selectedId: string | undefined;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  search: string;
  onSearchChange: (waarde: string) => void;
  onSelect: (id?: string) => void;
  onPaginaChange: (pagina: number) => void;
  exportLink: string;
}) {
  // No CardHeader: the page title above both panes already says "Seizoenen", and repeating it
  // on the list card names the same thing twice.
  if (isPending || !page) {
    return (
      <Card>
        <CardContent className="space-y-4 pt-6">
          <DataTableSkeleton
            label={t("seizoenen.label")}
            columns={columns}
            rows={4}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <DataTable
          label={t("seizoenen.label")}
          data={page.items}
          columns={columns}
          getRowId={(row) => row.id}
          selectedId={selectedId}
          onSelect={onSelect}
          empty={t("seizoenen.leeg")}
          exportHref={exportLink}
          serverDriven={{
            sorting,
            onSortingChange,
            globalFilter: search,
            onGlobalFilterChange: onSearchChange,
          }}
        />
        <Pager
          page={page.page}
          pageSize={page.pageSize}
          totalCount={page.totalCount}
          onPageChange={onPaginaChange}
        />
      </CardContent>
    </Card>
  );
}

/** The start/end date grid shared by the create dialog and the detail form. */
function PeriodeVelden({
  idPrefix,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}: {
  idPrefix: string;
  startDate: string;
  endDate: string;
  onStartDateChange: (waarde: string) => void;
  onEndDateChange: (waarde: string) => void;
}) {
  const { t } = useTranslation("wedstrijden");

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="grid gap-2">
        <Label htmlFor={`${idPrefix}-start`}>{t("seizoenen.start")}</Label>
        <Input
          id={`${idPrefix}-start`}
          type="date"
          value={startDate}
          onChange={(event) => onStartDateChange(event.target.value)}
          required
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor={`${idPrefix}-eind`}>{t("seizoenen.eind")}</Label>
        <Input
          id={`${idPrefix}-eind`}
          type="date"
          value={endDate}
          onChange={(event) => onEndDateChange(event.target.value)}
          required
        />
      </div>
    </div>
  );
}

/** A modal, not a route: three fields that end back on the list behind them. */
function NieuwSeizoen({
  onAangemaakt,
}: {
  onAangemaakt: (id: string) => void;
}) {
  const { t } = useTranslation(["wedstrijden", "common"]);
  const create = useCreateSeizoen();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Closing throws the half-typed seizoen away rather than keeping it for next time: reopening
  // to last week's abandoned draft is the surprise, not the empty form.
  function wissel(volgende: boolean) {
    setOpen(volgende);
    if (!volgende) {
      setName("");
      setStartDate("");
      setEndDate("");
      create.reset();
    }
  }

  return (
    <Dialog open={open} onOpenChange={wissel}>
      <DialogTrigger asChild>
        <Button>
          <Plus />
          {t("seizoenen.aanmaken")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("seizoenen.aanmaken")}</DialogTitle>
          <DialogDescription>{t("seizoenen.aanmakenUitleg")}</DialogDescription>
        </DialogHeader>

        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate(
              { name, startDate, endDate },
              {
                onSuccess: (seizoen) => {
                  wissel(false);
                  onAangemaakt(seizoen.id);
                },
              },
            );
          }}
        >
          <div className="grid gap-2">
            <Label htmlFor="nieuw-seizoen-naam">{t("seizoenen.naam")}</Label>
            <Input
              id="nieuw-seizoen-naam"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("seizoenen.naamPlaceholder")}
              required
            />
          </div>
          <PeriodeVelden
            idPrefix="nieuw-seizoen"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <FormActions>
            {create.error && (
              <p className="text-sm text-destructive">{create.error.message}</p>
            )}
            <Button type="submit" disabled={create.isPending}>
              {create.isPending
                ? t("common:actions.busy")
                : t("common:actions.create")}
            </Button>
          </FormActions>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SeizoenDetail({
  seizoen,
  onVerwijderd,
}: {
  seizoen: Seizoen;
  onVerwijderd: () => void;
}) {
  const { t } = useTranslation(["wedstrijden", "common"]);
  const update = useUpdateSeizoen();
  const remove = useDeleteSeizoen();

  const [name, setName] = useState(seizoen.name);
  const [startDate, setStartDate] = useState(seizoen.startDate);
  const [endDate, setEndDate] = useState(seizoen.endDate);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{seizoen.name}</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              update.mutate({ id: seizoen.id, name, startDate, endDate });
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor="seizoen-naam">{t("seizoenen.naam")}</Label>
              <Input
                id="seizoen-naam"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
              />
            </div>
            <PeriodeVelden
              idPrefix="seizoen"
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
            />
            <p className="text-xs text-muted-foreground">
              {t("seizoenen.datumsUitleg")}
            </p>
            <FormActions>
              {update.error && (
                <p className="text-sm text-destructive">
                  {update.error.message}
                </p>
              )}
              <Button asChild variant="secondary">
                <Link to="/wedstrijddagen" search={{ seizoen: seizoen.id }}>
                  <CalendarDays />
                  {t("seizoenen.naarDagen")}
                </Link>
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending
                  ? t("common:actions.busy")
                  : t("common:actions.save")}
              </Button>
            </FormActions>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("seizoenen.verwijderenTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t("seizoenen.verwijderenUitleg")}
          </p>
          <FormActions>
            {remove.error && (
              <p className="text-sm text-destructive">{remove.error.message}</p>
            )}
            <ConfirmDelete
              trigger={
                <Button variant="destructive" disabled={remove.isPending}>
                  {remove.isPending
                    ? t("common:actions.busy")
                    : t("common:actions.delete")}
                </Button>
              }
              title={t("seizoenen.verwijderenVraag", { naam: seizoen.name })}
              description={t("seizoenen.verwijderenDescription")}
              confirmLabel={t("seizoenen.verwijderenConfirm")}
              onConfirm={() =>
                remove.mutate(seizoen.id, { onSuccess: onVerwijderd })
              }
            />
          </FormActions>
        </CardContent>
      </Card>
    </div>
  );
}
